/**
 * resolveAssistantName — resolve the assistant / workspace display name.
 *
 * The owner's configured workspace name lives on `UserProfile.containerName`
 * (camelCase — built by `SessionProvider` from the API's snake_case
 * `container_name`). When it is absent we fall back to the product default,
 * "Xavior".
 *
 * Regression guard (PR #3266): the display sites — `ProfileModal` and
 * `ArchivedModal` — previously read
 * `profile.container_name` (snake_case), a property that does not exist on
 * `UserProfile`. Every read resolved to `undefined`, so the real name silently
 * fell back to "Xavior". Centralising the read here means the field is
 * referenced once, against the typed `UserProfile`: drifting it back to
 * snake_case becomes a compile error (`TS2551`), not a silent runtime
 * fallback. The behaviour is pinned by `assistantName.test.ts`.
 */
import type { UserProfile } from '@/types/session'

/** Product default shown when no workspace / assistant name is configured. */
export const DEFAULT_ASSISTANT_NAME = 'Xavior'

/**
 * Resolve the display name for the assistant from the session profile.
 *
 * Returns the configured `containerName` when present, otherwise
 * `DEFAULT_ASSISTANT_NAME`. Treats `null`, `undefined`, and `''` as "not
 * configured" — identical to the original `profile?.containerName || 'Xavior'`
 * expression it replaces.
 */
export function resolveAssistantName(
  profile: Pick<UserProfile, 'containerName'> | null | undefined,
): string {
  return profile?.containerName || DEFAULT_ASSISTANT_NAME
}
